import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Box,
  Loader2,
  PackageOpen,
  Warehouse,
  ArrowRightLeft,
  Skull,
  Gift,
  HeartHandshake,
  Tag,
  Trash,
  Eraser,
} from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";

/**
 * Dialog unifié de retrait de stock — Phase 63 (HACCP-safe).
 *
 * 5 motifs :
 *   - DLC_EXPIRED    : Perte DLC → OUT
 *   - BROKEN         : Casse / rebut → OUT
 *   - BLOCKED        : Barquette bloquée en machine → OUT
 *   - RETURN_CONTHEY : Retour Conthey → TRANSFER (destination auto-résolue)
 *   - TRANSFER       : Transfert vers autre dépôt → TRANSFER (destination requise)
 *
 * Remplace l'ancien bouton 🗑️ qui faisait DELETE /lots/{id} (effaçait l'historique HACCP).
 *
 * Props :
 *   - lot              : { id, product_id, lot_number, quantity, dlc, distributor_id, ... }
 *   - productName      : nom du produit (pour affichage)
 *   - distributorName  : nom du dépôt source (pour affichage)
 *   - distributors     : liste complète des distributeurs (pour le Select destination)
 *   - onDone           : callback appelé après succès (refresh parent)
 *   - trigger          : élément React déclencheur (typiquement un Button avec icône)
 *   - testIdPrefix     : préfixe pour les data-testid
 */
const REASONS = [
  {
    key: "DLC_EXPIRED",
    label: "Perte DLC",
    description: "DLC dépassée → poubelle. Génère un mouvement OUT.",
    icon: AlertTriangle,
    color: "text-[#C05C47]",
    bgColor: "bg-[#FCEEEA]",
    borderColor: "border-[#E8C1B4]",
    needsDestination: false,
    scope: "PARTNER",
  },
  {
    key: "BROKEN",
    label: "Casse / rebut",
    description: "Barquette cassée, abîmée, rejetée. Préciser la cause.",
    icon: Skull,
    color: "text-[#C05C47]",
    bgColor: "bg-[#FCEEEA]",
    borderColor: "border-[#E8C1B4]",
    needsDestination: false,
    scope: "BOTH",
  },
  {
    key: "BLOCKED",
    label: "Bloquée en machine",
    description: "Barquette coincée dans le distributeur ou inaccessible. Reportée par le partenaire terrain.",
    icon: PackageOpen,
    color: "text-[#8C6B2F]",
    bgColor: "bg-[#FBF4E9]",
    borderColor: "border-[#E8D3A8]",
    needsDestination: false,
    scope: "PARTNER",
  },
  {
    key: "RETURN_CONTHEY",
    label: "Retour Conthey",
    description: "Renvoi vers l'entrepôt central. Destination Conthey (résolue automatiquement).",
    icon: Warehouse,
    color: "text-[#1F4E3D]",
    bgColor: "bg-[#EAF4EE]",
    borderColor: "border-[#C5DBCF]",
    needsDestination: false, // auto-résolu côté backend
    scope: "PARTNER",
  },
  {
    key: "TRANSFER",
    label: "Transfert vers…",
    description: "Déplacement vers un autre distributeur/dépôt. Destination à préciser.",
    icon: ArrowRightLeft,
    color: "text-[#1F4E3D]",
    bgColor: "bg-[#EAF4EE]",
    borderColor: "border-[#C5DBCF]",
    needsDestination: true,
    scope: "BOTH",
  },
  // Phase 101ej (Feb 2026) — Barquettes remises à clients/prospects
  // à titre d'essai ou de dégustation (usage commercial B2B).
  {
    key: "SAMPLE",
    label: "Échantillon / dégustation",
    description: "Barquettes remises à un client/prospect pour essai commercial. Préciser le destinataire dans les notes.",
    icon: Gift,
    color: "text-[#8C6B2F]",
    bgColor: "bg-[#FBF4E9]",
    borderColor: "border-[#E8D3A8]",
    needsDestination: false,
    scope: "PARTNER",
  },
  // Phase 101ea.19 (Feb 2026) — Sorties finales depuis Stock Conthey (dépôt
  // central). Ces motifs ferment le cycle de vie d'une barquette :
  //   • DESTROYED       : seule vraie perte comptable (jetée physiquement)
  //   • GIFT_STAFF      : offerte au personnel → valorisation RH (pas perte)
  //   • GIFT_SOCIAL     : offerte à un home / entité sociale (pas perte)
  //   • SALE_DISCOUNTED : vendue à prix cassé à un client / home (vente)
  {
    key: "DESTROYED",
    label: "Destruction (poubelle)",
    description: "DLC physique dépassée → jetée. Seul motif compté comme perte comptable.",
    icon: Trash,
    color: "text-[#C05C47]",
    bgColor: "bg-[#FCEEEA]",
    borderColor: "border-[#E8C1B4]",
    needsDestination: false,
    scope: "STOCK_CONTHEY",
  },
  {
    key: "GIFT_STAFF",
    label: "Don personnel",
    description: "Barquette offerte au personnel. Valorisation RH, pas une perte comptable.",
    icon: Gift,
    color: "text-[#1F4E3D]",
    bgColor: "bg-[#EAF4EE]",
    borderColor: "border-[#C5DBCF]",
    needsDestination: false,
    scope: "STOCK_CONTHEY",
  },
  {
    key: "GIFT_SOCIAL",
    label: "Don entités sociales",
    description: "Don à un home, EMS, association. Valorisation sociale, pas une perte comptable.",
    icon: HeartHandshake,
    color: "text-[#1F4E3D]",
    bgColor: "bg-[#EAF4EE]",
    borderColor: "border-[#C5DBCF]",
    needsDestination: false,
    scope: "STOCK_CONTHEY",
  },
  {
    key: "SALE_DISCOUNTED",
    label: "Vente soldée",
    description: "Barquette vendue à prix cassé (client, home, personnel). C'est une vente, pas une perte.",
    icon: Tag,
    color: "text-[#8C6B2F]",
    bgColor: "bg-[#FBF4E9]",
    borderColor: "border-[#E8D3A8]",
    needsDestination: false,
    scope: "STOCK_CONTHEY",
  },
  {
    key: "ENTRY_ERROR",
    label: "Erreur de saisie",
    description: "Barquettes saisies par erreur (OF, lot…). Corrige le stock sans compter de perte. Préciser l'erreur.",
    icon: Eraser,
    color: "text-[#575A57]",
    bgColor: "bg-[#F4F4F0]",
    borderColor: "border-[#D6D6D0]",
    needsDestination: false,
    scope: "BOTH",
  },
];

const NOTES_REQUIRED = ["BROKEN", "ENTRY_ERROR"];

const DISPOSAL_KEYS = ["DESTROYED", "BROKEN"];
const DISPOSAL_CHANNELS = [
  ["DECHETS_ORGANIQUES", "Déchets organiques de cuisine (collecte communale)"],
  ["METHANISATION", "Méthanisation / biogaz"],
  ["INCINERATION", "Incinération (UIOM)"],
  ["PRESTATAIRE", "Prestataire agréé (sous-produits animaux)"],
  ["AUTRE", "Autre"],
];

export default function WithdrawLotDialog({
  lot,
  productName,
  distributorName,
  distributors = [],
  onDone,
  trigger,
  testIdPrefix = "withdraw",
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [notes, setNotes] = useState("");
  const [disposalChannel, setDisposalChannel] = useState("");
  const [disposalRef, setDisposalRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset à chaque ouverture (déclenché dans onOpenChange pour éviter setState-in-effect)
  const handleOpenChange = (next) => {
    if (next) {
      setReason(null);
      setQuantity(String(lot?.quantity ?? ""));
      setDestinationId("");
      setNotes("");
      setDisposalChannel("");
      setDisposalRef("");
    }
    setOpen(next);
  };

  const selectedReason = useMemo(() => REASONS.find((r) => r.key === reason), [reason]);

  // Phase 101ea.19 — Détecte si le lot est actuellement au Stock Conthey
  // (dépôt central). Si oui, on propose les 4 motifs de sortie finale
  // (destruction / don personnel / don social / vente soldée) au lieu des
  // motifs terrain (DLC_EXPIRED, BLOCKED, RETURN_CONTHEY, SAMPLE).
  const isStockConthey = useMemo(() => {
    const src = distributors.find((d) => d.id === lot?.distributor_id);
    if (!src) return false;
    return src.code === "Stock" || /^Stock\s+Conthey/i.test(src.name || "");
  }, [distributors, lot?.distributor_id]);

  const applicableReasons = useMemo(() => {
    const target = isStockConthey ? "STOCK_CONTHEY" : "PARTNER";
    return REASONS.filter((r) => r.scope === target || r.scope === "BOTH");
  }, [isStockConthey]);

  // Filtre les destinations : exclure le dépôt source actuel
  const destinationOptions = useMemo(() => {
    return distributors.filter((d) => d.id !== lot?.distributor_id);
  }, [distributors, lot?.distributor_id]);

  const conthey = useMemo(
    () => distributors.find(
      (d) => d.type === "WAREHOUSE" && /Conthey/i.test(d.name || "")
    ) || distributors.find((d) => d.type === "WAREHOUSE"),
    [distributors]
  );

  const notesRequired = NOTES_REQUIRED.includes(selectedReason?.key);
  const missing = (() => {
    if (!selectedReason) return "Choisissez un motif.";
    const q = Number(quantity);
    if (!Number.isFinite(q) || q <= 0) return "Indiquez une quantité positive.";
    if (q > (lot?.quantity ?? 0)) return `Quantité supérieure au disponible (${lot?.quantity}).`;
    if (selectedReason.needsDestination && !destinationId) return "Choisissez le dépôt destination.";
    if (notesRequired && !notes.trim()) return "Remplissez le champ « Notes / commentaire » (obligatoire pour ce motif).";
    if (selectedReason.key === "DESTROYED" && !disposalChannel) return "Choisissez la filière d'élimination.";
    return null;
  })();
  const canSubmit = !missing;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        reason: selectedReason.key,
        quantity: Number(quantity),
        destination_distributor_id: selectedReason.needsDestination ? destinationId : null,
        notes: notes.trim() || "",
        disposal_channel: DISPOSAL_KEYS.includes(selectedReason.key) ? disposalChannel || null : null,
        disposal_ref: DISPOSAL_KEYS.includes(selectedReason.key) ? disposalRef.trim() : "",
      };
      await api.post(`/lots/${lot.id}/withdraw`, payload);
      toast.success(`${selectedReason.label} enregistrée — historique HACCP préservé`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du retrait");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div onClick={(e) => { e.stopPropagation(); handleOpenChange(true); }}>
        {trigger}
      </div>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Box className="w-5 h-5 text-[#1F4E3D]" />
            Retirer du stock
          </DialogTitle>
          <DialogDescription>
            Lot <strong className="font-mono">{lot?.lot_number}</strong>
            {productName ? <> · <strong>{productName}</strong></> : null}
            {distributorName ? <> · {distributorName}</> : null}
            {lot?.dlc ? <> · DLC <span className="font-mono">{lot.dlc}</span></> : null}
            <br />
            <span className="text-xs text-[#A3A6A3]">
              Quantité disponible : <strong className="text-[#1A1C1A] font-mono">{lot?.quantity}</strong>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Motif */}
          <div>
            <Label className="mb-2 block">
              Motif du retrait *
              {isStockConthey && (
                <span className="ml-2 text-[10px] font-normal text-[#1F4E3D] bg-[#EAF4EE] px-1.5 py-0.5 rounded">
                  Sortie Stock Conthey
                </span>
              )}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {applicableReasons.map((r) => {
                const Icon = r.icon;
                const selected = reason === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setReason(r.key)}
                    className={`flex items-start gap-2 p-3 border rounded text-left transition-all ${
                      selected
                        ? `${r.borderColor} ${r.bgColor} ring-2 ring-offset-1 ring-[#1F4E3D]/30`
                        : "border-[#E5E5E0] bg-white hover:border-[#1F4E3D]/30"
                    }`}
                    data-testid={`${testIdPrefix}-reason-${r.key}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${r.color}`} />
                    <div>
                      <div className="text-xs font-semibold text-[#1A1C1A]">{r.label}</div>
                      <div className="text-[10px] text-[#575A57] mt-0.5 leading-relaxed">{r.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantité */}
          {selectedReason && (
            <div>
              <Label className="mb-1.5 block">Quantité à retirer *</Label>
              <NumericInput
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={String(lot?.quantity ?? "")}
                data-testid={`${testIdPrefix}-quantity`}
              />
              <p className="text-[10px] text-[#A3A6A3] mt-1">
                Max : <span className="font-mono">{lot?.quantity}</span>. Le solde restera sur le lot.
              </p>
            </div>
          )}

          {/* Destination — uniquement pour TRANSFER */}
          {selectedReason?.needsDestination && (
            <div>
              <Label className="mb-1.5 block">Dépôt destination *</Label>
              <Select value={destinationId} onValueChange={setDestinationId}>
                <SelectTrigger data-testid={`${testIdPrefix}-destination`}>
                  <SelectValue placeholder="Choisir un dépôt…" />
                </SelectTrigger>
                <SelectContent>
                  {destinationOptions.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] uppercase text-[#A3A6A3] font-mono">{d.type}</span>
                        {d.name}
                        {d.city ? <span className="text-[10px] text-[#A3A6A3]">· {d.city}</span> : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Info Conthey pour RETURN_CONTHEY */}
          {selectedReason?.key === "RETURN_CONTHEY" && (
            <div className="p-3 rounded bg-[#EAF4EE] border border-[#C5DBCF] text-xs text-[#1F4E3D]" data-testid={`${testIdPrefix}-conthey-info`}>
              <Warehouse className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Destination : <strong>{conthey?.name || "Entrepôt central (résolu côté serveur)"}</strong>
            </div>
          )}

          {DISPOSAL_KEYS.includes(selectedReason?.key) && (
            <div className="grid sm:grid-cols-2 gap-3 p-3 rounded border border-[#E8C1B4] bg-[#FCEEEA]/40" data-testid={`${testIdPrefix}-disposal`}>
              <div>
                <Label className="mb-1.5 block">
                  Filière d&apos;élimination{selectedReason?.key === "DESTROYED" && <span className="text-[#C05C47]"> *</span>}
                </Label>
                <Select value={disposalChannel} onValueChange={setDisposalChannel}>
                  <SelectTrigger data-testid={`${testIdPrefix}-disposal-channel`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {DISPOSAL_CHANNELS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">N° bon d&apos;élimination (optionnel)</Label>
                <Input value={disposalRef} onChange={(e) => setDisposalRef(e.target.value)} placeholder="ex. bon prestataire" data-testid={`${testIdPrefix}-disposal-ref`} />
              </div>
              <p className="sm:col-span-2 text-[11px] text-[#575A57]">Inscrit au registre HACCP « Destructions & congélations ».</p>
            </div>
          )}

          {/* Notes — obligatoire pour BROKEN, optionnel sinon */}
          <div>
            <Label className="mb-1.5 block">
              Notes / commentaire
              {notesRequired && <span className="text-[#C05C47]"> *</span>}
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                selectedReason?.key === "BROKEN"
                  ? "Cause de la casse (chute, défaut, contamination…) — obligatoire"
                  : selectedReason?.key === "ENTRY_ERROR"
                  ? "Ex. : OF du 12/10 saisi deux fois — obligatoire"
                  : selectedReason?.key === "BLOCKED"
                  ? "Détails sur le blocage (slot bloqué, défaillance technique…)"
                  : selectedReason?.key === "SAMPLE"
                  ? "Nom du client/prospect + contexte (ex: Bistro X, dégustation 12/02/2026)"
                  : "Optionnel — toute info utile pour l'audit"
              }
              rows={2}
              data-testid={`${testIdPrefix}-notes`}
            />
          </div>

          {/* Récap visuel */}
          {selectedReason && (
            <div
              className={`p-3 rounded border ${selectedReason.borderColor} ${selectedReason.bgColor}`}
              data-testid={`${testIdPrefix}-summary`}
            >
              <div className="flex items-center gap-2 text-xs">
                <selectedReason.icon className={`w-3.5 h-3.5 ${selectedReason.color}`} />
                <span className={`font-semibold ${selectedReason.color}`}>{selectedReason.label}</span>
                <Badge variant="outline" className="text-[10px]">
                  {selectedReason.needsDestination || selectedReason.key === "RETURN_CONTHEY" ? "TRANSFER" : "OUT"}
                </Badge>
              </div>
              <div className="text-[10px] text-[#575A57] mt-1 leading-relaxed">
                {selectedReason.description}
              </div>
            </div>
          )}
        </div>

        {missing && selectedReason && (
          <p className="text-xs text-[#C05C47] text-right" data-testid={`${testIdPrefix}-missing`}>{missing}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid={`${testIdPrefix}-confirm`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirmer le retrait
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
